import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { Eye, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import cn from 'classnames';

import { useUpdateAgentMutation } from '../../redux/services/agents/agentsApi';
import { tokenRefreshed } from '../../redux/services/api';
import { useGetUserInfoMutation } from '../../redux/services/user/userApi';
import { useGetCountriesQuery } from '../../redux/services/data/countriesApi';
import { useGetCitiesForCountryQuery } from '../../redux/services/data/citiesApi';
import {
  selectUserInfo,
  selectUserToken,
} from '../../redux/features/user/userSelectors';
import { SelectInput } from '../../components/form/inputs/SelectInput';
import { Loader } from '../../components/ui/Loader';
import { applyValidationErrors } from '../../utils/data';
import { setCookie } from '../../utils/cookies';
import { EMPTY_ARRAY, colors, errorMessages } from '../../data/constants';
import {
  baseFields,
  genderOptions,
  getPhotoError,
  getProfileValues,
  passwordSchema,
  PHOTO_TYPES,
  profileSchema,
} from '../../lib/mocks/profile-mock';

export const getInitials = userInfo => {
  const initials = `${userInfo?.first_name?.[0] ?? ''}${userInfo?.last_name?.[0] ?? ''}`;
  return initials.toUpperCase() || userInfo?.email?.[0]?.toUpperCase() || '?';
};

export const Avatar = ({ src, initials, className }) => (
  <div
    className={cn(
      'flex items-center justify-center rounded-full bg-purpleColor text-white font-semibold flex-shrink-0 overflow-hidden',
      className
    )}
  >
    {src ? (
      <img
        src={src}
        alt=""
        className="w-full h-full object-cover"
      />
    ) : (
      <span>{initials}</span>
    )}
  </div>
);

const inputClass = hasError =>
  cn(
    'w-full h-11 md:h-10 rounded-md border bg-white px-3 text-base md:text-sm text-blackColor outline-none transition-shadow focus:border-purpleColor focus:ring-[3px] focus:ring-purpleColor/20',
    hasError ? 'border-redColor' : 'border-gray-300'
  );

const Field = ({ id, label, optional, hint, error, children }) => (
  <div className="flex flex-col gap-1.5 min-w-0">
    <label
      htmlFor={id}
      className="flex items-baseline gap-1.5 text-[13px] font-semibold text-blackColor"
    >
      {label}
      {optional && (
        <span className="text-xs font-normal text-darkGrayColor/70">
          Optional
        </span>
      )}
    </label>
    {children}
    {error ? (
      <span className="text-xs text-redColor">{error}</span>
    ) : (
      hint && <span className="text-xs text-darkGrayColor">{hint}</span>
    )}
  </div>
);

const SubmitButton = ({ isLoading, children }) => (
  <div className="flex justify-end md:border-t md:border-gray-100 md:pt-[18px]">
    <button
      type="submit"
      disabled={isLoading}
      className="flex items-center justify-center gap-2 w-full md:w-auto h-12 md:h-10 px-5 rounded-md bg-purpleColor text-white text-base md:text-sm font-medium hover:opacity-90 disabled:opacity-60 transition-opacity"
    >
      {isLoading && <Loader color={colors.whiteColor} />}
      <span>{children}</span>
    </button>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-xs uppercase tracking-wider text-darkGrayColor font-medium">
    {children}
  </h2>
);

// Validate on submit only; editing a field clears its error.
const useFieldSetter = formik => (name, value) => {
  formik.setFieldValue(name, value, false);
  formik.setFieldError(name, undefined);
};

export const ProfileForm = () => {
  const userInfo = useSelector(selectUserInfo);
  const token = useSelector(selectUserToken);
  const [updateAgent, { isLoading }] = useUpdateAgentMutation();
  const [getUserInfo] = useGetUserInfoMutation();

  const initialValues = useMemo(
    () => ({ ...getProfileValues(userInfo), image: null, current_password: '' }),
    [userInfo]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: profileSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setErrors }) => {
      const response = await updateAgent({
        token,
        id: userInfo.id,
        data: values,
      });

      if (response.error) {
        applyValidationErrors(response.error, setErrors, errorMessages.wentWrong);
        return;
      }

      toast.success('Profile updated');
      getUserInfo(token);
    },
  });

  const { values, errors } = formik;
  const setValue = useFieldSetter(formik);

  const { data: countries = EMPTY_ARRAY } = useGetCountriesQuery();
  const { data: cities = EMPTY_ARRAY } = useGetCitiesForCountryQuery(
    { country_id: values.country_id },
    { skip: !values.country_id }
  );

  const selectedCountry = useMemo(
    () => countries.filter(c => c.value.toString() === values.country_id.toString()),
    [countries, values.country_id]
  );
  const selectedCity = useMemo(
    () => cities.filter(c => c.value.toString() === values.city_id.toString()),
    [cities, values.city_id]
  );

  // BE asks for the current password only when the email changes
  const isEmailChanged = values.email.trim() !== (userInfo?.email ?? '');

  const [photoPreview, setPhotoPreview] = useState(null);
  useEffect(() => {
    if (!values.image) {
      setPhotoPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(values.image);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [values.image]);
  const photoSrc = photoPreview || userInfo?.image;

  const handlePhotoPick = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const photoError = getPhotoError(file);
    if (photoError) {
      formik.setFieldError('image', photoError);
      return;
    }
    setValue('image', file);
  };

  const handleCountryChange = ([item]) => {
    setValue('country_id', item?.value ?? '');
    setValue('city_id', '');
  };

  const handleCityChange = ([item]) => setValue('city_id', item?.value ?? '');

  return (
    <form
      onSubmit={formik.handleSubmit}
      noValidate
      className="flex flex-col gap-[18px] md:gap-[22px]"
      data-testid="settings-profile-form"
    >
      <SectionTitle>Profile</SectionTitle>

      <div className="flex items-center gap-3.5 md:gap-[18px]">
        <Avatar
          src={photoSrc}
          initials={getInitials(values)}
          className="w-16 h-16 md:w-[72px] md:h-[72px] text-xl md:text-[22px]"
        />
        <div className="flex flex-col gap-2 min-w-0 flex-1">
          <div className="flex flex-wrap gap-2">
            <label className="flex items-center gap-2 h-10 md:h-9 px-3.5 rounded-md border border-gray-300 bg-white text-sm text-blackColor cursor-pointer hover:bg-lightestGrayColor transition-colors">
              <Upload size={16} />
              <span>{photoSrc ? 'Change photo' : 'Upload photo'}</span>
              <input
                type="file"
                accept={PHOTO_TYPES.join(',')}
                onChange={handlePhotoPick}
                className="hidden"
              />
            </label>
            {values.image && (
              <button
                type="button"
                onClick={() => setValue('image', null)}
                title="Discard new photo"
                aria-label="Discard new photo"
                className="flex items-center justify-center w-10 h-10 md:w-9 md:h-9 rounded-md border border-gray-300 bg-white text-darkGrayColor hover:bg-redColor/5 hover:text-redColor transition-colors"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
          {errors.image ? (
            <span className="text-xs text-redColor">{errors.image}</span>
          ) : (
            <span className="text-xs text-darkGrayColor">
              JPEG or PNG, up to 3 MB
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-y-3.5 md:grid-cols-2 md:gap-4">
        {baseFields.map(field => (
          <Field
            key={field.name}
            id={`profile-${field.name}`}
            label={field.label}
            error={errors[field.name]}
          >
            <input
              id={`profile-${field.name}`}
              type={field.type}
              autoComplete={field.autoComplete}
              value={values[field.name]}
              onChange={e => setValue(field.name, e.target.value)}
              className={inputClass(errors[field.name])}
            />
          </Field>
        ))}

        {isEmailChanged && (
          <Field
            id="profile-email-current-password"
            label="Current password"
            hint="Needed to change your email"
            error={errors.current_password}
          >
            <input
              id="profile-email-current-password"
              type="password"
              autoComplete="current-password"
              value={values.current_password}
              onChange={e => setValue('current_password', e.target.value)}
              className={inputClass(errors.current_password)}
            />
          </Field>
        )}

        <Field
          label="Country"
          error={errors.country_id}
        >
          <SelectInput
            options={countries}
            initialSelected={selectedCountry}
            onSelect={handleCountryChange}
            onRemove={handleCountryChange}
            placeholder="Select country"
            hasError={!!errors.country_id}
            isBoxed
          />
        </Field>

        <Field
          label="City"
          error={errors.city_id}
        >
          <SelectInput
            options={cities}
            initialSelected={selectedCity}
            onSelect={handleCityChange}
            onRemove={handleCityChange}
            placeholder={values.country_id ? 'Select city' : 'Select country first'}
            hasError={!!errors.city_id}
            isSearchable
            isBoxed
            isDisabled={!values.country_id}
          />
        </Field>
      </div>

      <Field
        label="Gender"
        optional
      >
        <ToggleGroup.Root
          type="single"
          value={values.gender === '' ? '' : String(values.gender)}
          onValueChange={v => v && setValue('gender', Number(v))}
          className="grid grid-cols-3 gap-0.5 w-full md:max-w-[360px] rounded-lg bg-lightGrayColor p-[3px]"
        >
          {genderOptions.map(option => (
            <ToggleGroup.Item
              key={option.value}
              value={String(option.value)}
              className="h-[38px] md:h-[34px] rounded-md text-base md:text-sm text-darkGrayColor data-[state=on]:bg-white data-[state=on]:text-blackColor data-[state=on]:shadow-sm transition-colors"
            >
              {option.title}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>
      </Field>

      <SubmitButton isLoading={isLoading}>Save</SubmitButton>
    </form>
  );
};

export const PasswordForm = () => {
  const userInfo = useSelector(selectUserInfo);
  const dispatch = useDispatch();
  const token = useSelector(selectUserToken);
  const [updateAgent, { isLoading }] = useUpdateAgentMutation();
  const [isVisible, setIsVisible] = useState(false);

  const formik = useFormik({
    initialValues: { current_password: '', password: '', password_confirmation: '' },
    validationSchema: passwordSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setErrors, resetForm }) => {
      const response = await updateAgent({
        token,
        id: userInfo.id,
        data: { ...getProfileValues(userInfo), ...values },
      });

      if (response.error) {
        applyValidationErrors(response.error, setErrors, errorMessages.wentWrong);
        return;
      }

      // The change ends every older session, this one included; continue on the new token.
      const { access_token, refresh_expires_in } = response.data;
      setCookie('token', access_token, refresh_expires_in);
      dispatch(tokenRefreshed(access_token));

      toast.success('Password updated');
      resetForm();
      setIsVisible(false);
    },
  });

  const { values, errors } = formik;
  const setValue = useFieldSetter(formik);

  const passwordFields = [
    { name: 'current_password', label: 'Current password', autoComplete: 'current-password' },
    { name: 'password', label: 'New password', hint: 'At least 6 characters' },
    { name: 'password_confirmation', label: 'Confirm password' },
  ];

  return (
    <form
      onSubmit={formik.handleSubmit}
      noValidate
      className="flex flex-col gap-[18px] md:gap-[22px]"
      data-testid="settings-password-form"
    >
      <SectionTitle>Change password</SectionTitle>

      <div className="grid grid-cols-1 gap-y-3.5 md:grid-cols-2 md:gap-4">
        {passwordFields.map(field => (
          <Field
            key={field.name}
            id={`profile-${field.name}`}
            label={field.label}
            hint={field.hint}
            error={errors[field.name]}
          >
            <div className="relative">
              <input
                id={`profile-${field.name}`}
                type={isVisible ? 'text' : 'password'}
                autoComplete={field.autoComplete ?? 'new-password'}
                value={values[field.name]}
                onChange={e => setValue(field.name, e.target.value)}
                className={cn(inputClass(errors[field.name]), 'pr-11')}
              />
              <button
                type="button"
                onClick={() => setIsVisible(prev => !prev)}
                aria-label={isVisible ? 'Hide password' : 'Show password'}
                aria-pressed={isVisible}
                className={cn(
                  'absolute right-0.5 top-0.5 flex items-center justify-center w-10 h-10 md:w-9 md:h-9 rounded-md hover:text-purpleColor transition-colors',
                  isVisible ? 'text-purpleColor' : 'text-darkGrayColor/60'
                )}
              >
                <Eye size={18} />
              </button>
            </div>
          </Field>
        ))}
      </div>

      <SubmitButton isLoading={isLoading}>Update password</SubmitButton>
    </form>
  );
};
